import { useState, useEffect } from 'react';
import { guildsAPI, staffAPI, shiftsAPI } from '../../api';
import api from '../../api';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

function StatCard({ icon, label, value, sub, color, gradient }) {
  return (
    <div style={{
      background: gradient || 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: '16px', padding: '24px',
      boxShadow: 'var(--shadow-card)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '-20px', right: '-20px',
        width: '80px', height: '80px', borderRadius: '50%',
        background: color || 'var(--accent-soft)', opacity: 0.3,
      }} />
      <div style={{ fontSize: '28px', marginBottom: '12px' }}>{icon}</div>
      <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '4px' }}>{label}</div>
      {sub && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>{sub}</div>}
    </div>
  );
}

function StaffRow({ staff, sym }) {
  const statusColor = { active: 'var(--success)', inactive: 'var(--text-muted)', suspended: 'var(--danger)', on_leave: 'var(--warning)' };
  const avatarUrl = staff.avatar_url || null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '12px 0', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        width: '38px', height: '38px', borderRadius: '50%',
        background: 'var(--gradient-accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '15px', fontWeight: 700, color: '#fff', flexShrink: 0,
      }}>
        {staff.display_name?.[0] || staff.username?.[0] || '?'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
          {staff.display_name || staff.username}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {staff.rank_name || 'No Rank'}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
          {parseFloat(staff.period_hours || 0).toFixed(1)}h
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {sym}{parseFloat(staff.current_balance || 0).toFixed(2)}
        </div>
      </div>
      <div style={{
        width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
        background: staff.is_clocked_in ? 'var(--success)' : statusColor[staff.status] || 'var(--text-muted)',
        boxShadow: staff.is_clocked_in ? '0 0 8px var(--success)' : 'none',
      }} />
    </div>
  );
}

export default function OverviewPage({ guild }) {
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const sym = guild?.currency_symbol || '$';

  useEffect(() => {
    Promise.all([
      staffAPI.getAll(guild.id),
      api.get(`/api/departments/${guild.id}`),
    ]).then(([staffRes, deptsRes]) => {
      setStaff(staffRes.data);
      setDepartments(deptsRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const activeStaff = staff.filter(s => s.status === 'active');
  const clockedIn = activeStaff.filter(s => s.is_clocked_in);
  const totalOwed = activeStaff.reduce((sum, s) => sum + parseFloat(s.current_balance || 0), 0);
  const periodHours = activeStaff.reduce((sum, s) => sum + parseFloat(s.period_hours || 0), 0);
  const topEarner = [...activeStaff].sort((a, b) => parseFloat(b.period_hours) - parseFloat(a.period_hours))[0];

  const nextReset = guild?.next_reset
    ? new Date(guild.next_reset).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not configured';

  // Simple hours chart data (last 7 days placeholder)
  const chartData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return {
      day: d.toLocaleDateString('en-US', { weekday: 'short' }),
      hours: Math.random() * 8,
    };
  });

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
      Loading overview...
    </div>
  );

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: '32px',
          color: 'var(--text-primary)', marginBottom: '6px',
        }}>Overview</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
          {guild.name} — Live staff and budget summary
        </p>
      </div>

      {/* Stat cards */}
      <div className="stat-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px', marginBottom: '32px',
      }}>
        <StatCard icon="🟢" label="Clocked In Now" value={`${clockedIn.length} / ${activeStaff.length}`} sub="active staff" />
        <StatCard icon="⏱️" label="Period Hours" value={`${periodHours.toFixed(1)}h`} sub={`Next reset: ${nextReset}`} />
        <StatCard icon="⚠️" label="Outstanding Balance" value={`${sym}${totalOwed.toFixed(2)}`} sub="total owed to staff" />
        <StatCard icon="👥" label="Total Staff" value={activeStaff.length} sub={`${staff.filter(s => s.status !== 'active').length} inactive`} />
        <StatCard icon="🏆" label="Top This Period" value={topEarner ? `${parseFloat(topEarner.period_hours).toFixed(1)}h` : '—'} sub={topEarner?.display_name || topEarner?.username || '—'} />
        <StatCard icon="💰" label="Currency" value={guild.currency_symbol || '$'} sub={guild.currency_name || 'Dollar'} />
      </div>

      {/* Main content grid */}
      <div className="overview-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px' }}>

        {/* Left: Chart + clocked in */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Hours chart */}
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '16px', padding: '24px',
            boxShadow: 'var(--shadow-card)',
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '20px' }}>
              📈 Hours This Week
            </h2>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="hoursGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--accent-primary)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--accent-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '8px', fontSize: '13px',
                  }}
                  formatter={(val) => [`${val.toFixed(1)}h`, 'Hours']}
                />
                <Area type="monotone" dataKey="hours" stroke="var(--accent-primary)" strokeWidth={2} fill="url(#hoursGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Currently clocked in */}
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '16px', padding: '24px',
            boxShadow: 'var(--shadow-card)',
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>
              🟢 Currently On The Clock ({clockedIn.length})
            </h2>
            {clockedIn.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '14px' }}>
                Nobody is currently clocked in.
              </div>
            ) : (
              clockedIn.map(s => <StaffRow key={s.id} staff={s} sym={sym} />)
            )}
          </div>
        </div>

        {/* Right: Staff list + budget */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Budget summary */}
          <div style={{
            background: 'var(--gradient-accent)',
            borderRadius: '16px', padding: '24px',
            color: '#fff',
          }}>
            <h2 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px', opacity: 0.9 }}>
              💵 Budget Summary
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Owed (unpaid)', value: `${sym}${totalOwed.toFixed(2)}` },
                { label: 'Period earnings', value: `${sym}${activeStaff.reduce((s, m) => s + parseFloat(m.current_balance || 0), 0).toFixed(2)}` },
                { label: 'Lifetime paid out', value: `${sym}${activeStaff.reduce((s, m) => s + parseFloat(m.total_paid_out || 0), 0).toFixed(2)}` },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', opacity: 0.85 }}>{item.label}</span>
                  <span style={{ fontSize: '15px', fontWeight: 700 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Full staff list */}
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: '16px', padding: '24px',
            boxShadow: 'var(--shadow-card)',
            flex: 1,
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
              👥 All Staff ({activeStaff.length})
            </h2>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {activeStaff.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', fontSize: '14px' }}>
                  No active staff found.
                </div>
              ) : (
                activeStaff.map(s => <StaffRow key={s.id} staff={s} sym={sym} />)
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Department Summary Cards */}
      {departments.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>🏢 Departments</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
            {departments.map(dept => (
              <div key={dept.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: '14px', padding: '18px', boxShadow: 'var(--shadow-card)',
                borderTop: `4px solid ${dept.color}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: dept.color }} />
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{dept.name}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  {[
                    { label: 'Staff', value: dept.staff_count || 0 },
                    { label: 'Hrs', value: `${parseFloat(dept.period_hours || 0).toFixed(1)}h` },
                    { label: 'Owed', value: `${sym}${parseFloat(dept.owed_balance || 0).toFixed(0)}` },
                  ].map(stat => (
                    <div key={stat.label} style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{stat.value}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
